import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Record, correct or clear the date behind one stamped box on the case chart (Sep 28 2026).
 *
 * Opens on today's date in Texas when the box is empty, because the common case is a person doing
 * the thing as they click. Clear exists because a stamp on the wrong case must be undoable without
 * a database edit — and it is a separate button rather than an emptied field, so it cannot happen
 * by accident.
 */
interface JourneyStampDialogProps {
  open: boolean;
  /** The step's full label, e.g. "Paperwork received from the court". */
  label: string;
  /** The date already recorded, 'YYYY-MM-DD', or null. */
  current: string | null;
  onClose: () => void;
  onSave: (date: string | null) => Promise<void>;
}

/** Today in the firm's own time zone, so an evening click does not land on tomorrow. */
function todayInTexas(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

const JourneyStampDialog: React.FC<JourneyStampDialogProps> = ({
  open,
  label,
  current,
  onClose,
  onSave,
}) => {
  const [date, setDate] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setDate(current ? current.slice(0, 10) : todayInTexas());
    setError(null);
  }, [open, current]);

  const save = async (value: string | null) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(value);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{label}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {current ? `Recorded for ${current.slice(0, 10)}.` : 'Not recorded yet.'}
        </Typography>
        <TextField
          label="Date"
          type="date"
          size="small"
          fullWidth
          disabled={saving}
          InputLabelProps={{ shrink: true }}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {current && (
          <Button color="error" disabled={saving} onClick={() => save(null)} sx={{ mr: 'auto' }}>
            Clear
          </Button>
        )}
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" disabled={saving || !date} onClick={() => save(date)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JourneyStampDialog;
